// cherry-picked from: https://github.com/mike-north/ember-api-actions
import { getOwner } from '@ember/application';
import type Owner from '@ember/owner';
// eslint-disable-next-line ember/use-ember-data-rfc-395-imports
import type Model from 'ember-data/model';

export type Hook<IN, OUT> = (this: Model, payload: IN) => OUT;

interface InstanceOperationOption<IN, OUT> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  after?: Hook<any, OUT>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  before?: Hook<IN, any>;
  path: string;
  type: 'POST' | 'PUT';
  urlType: 'updateRecord';
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function extractQueryParams(url: string) {
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) {
    return '';
  }

  return url.slice(queryIndex);
}

function getModelClass<M extends typeof Model>(record: InstanceType<M>): M {
  return record.constructor as M;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function buildURL(baseURL: string, path: string) {
  if (!path) {
    return baseURL;
  }

  const cleanURL = baseURL.split('?')[0] as string;
  const params = extractQueryParams(baseURL);

  if (cleanURL.charAt(cleanURL.length - 1) === '/') {
    return `${cleanURL}${path}/${params}`;
  }

  return `${cleanURL}/${path}/${params}`;
}

export default function apiAction<IN = unknown, OUT = unknown>(
  options: InstanceOperationOption<IN, OUT>,
) {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  return function executeApiAction(this: Model, payload: IN) {
    const { after, path, type, urlType } = options;
    const modelClass = getModelClass(this);
    const { modelName } = modelClass;
    const owner = getOwner(this) as Owner;
    const store = owner.lookup('service:store');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const adapter = store.adapterFor(modelName);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const baseURL = adapter.buildURL(
      modelName,
      this.id ?? null,
      this._createSnapshot(),
      urlType,
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const fullURL = buildURL(baseURL, path);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      adapter
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .ajax(fullURL, type, { data: payload })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .then((response: object) => {
          if (after && !this.isDestroyed) {
            return after.call(this, response);
          }

          return response;
        })
    );
  };
}
